import React, { useEffect, useState } from "react";
import PageLayout from "@/components/layout/PageLayout";
import { Hero, Section } from "@/components/layout/Sections";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ExternalLink, Trophy, Bike, ShoppingBag, Wrench, Calendar } from "lucide-react";
import TestRideForm from "@/components/TestRideForm";
import api, { mediaUrl } from "@/lib/api";

export default function MotoHome() {
  const [featuredEvent, setFeaturedEvent] = useState(null);

  useEffect(() => {
    api.get("/events")
      .then((r) => {
        const list = r.data || [];
        // Preferisci il prossimo evento (non concluso); fallback all'ultimo evento disponibile
        const upcoming = list.find((e) => !e.is_past);
        setFeaturedEvent(upcoming || list[0] || null);
      })
      .catch(() => setFeaturedEvent(null));
  }, []);

  return (
    <PageLayout>
      <Hero
        image="/showroom/moto-hero.webp"
        eyebrow="KTM · Husqvarna Motorcycles · GasGas Trial · Racing Team"
        title={<>Il nostro mondo<br />su due ruote</>}
        subtitle={<>Tre marchi, una filosofia. Adrenalina, prestazioni e comunità:<br />benvenuto nel mondo NSA Motors.</>}
      />

      {/* Promo Eventi — dinamico sull'ultimo/prossimo evento */}
      {featuredEvent && (
        <Section>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <Link
              to={`/moto/eventi/${featuredEvent.slug}`}
              className="group relative block overflow-hidden bg-gray-900 text-white border-2 border-[color:var(--nsa-orange)]"
              data-testid="evento-promo"
            >
              <div
                className="absolute inset-0 bg-cover bg-center opacity-45 scale-105 group-hover:scale-100 transition-transform duration-[900ms]"
                style={{
                  backgroundImage: featuredEvent.cover_image
                    ? `url(${mediaUrl(featuredEvent.cover_image)}), url(/placeholders/photo-slot.svg)`
                    : "url(/placeholders/photo-slot.svg)",
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" />
              <div
                className="absolute inset-0 opacity-[0.06] pointer-events-none"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(45deg, #FF6600 0 10px, transparent 10px 22px)",
                }}
              />
              <div className="relative px-6 py-8 md:px-12 md:py-14 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-[color:var(--nsa-orange)] mb-3">
                    <Calendar size={14} />
                    <span className="label-eyebrow tracking-[0.3em] text-xs">
                      {featuredEvent.is_past ? "Rivivi l'evento" : "Save the date"}
                    </span>
                  </div>
                  <h3 className="font-display uppercase text-3xl md:text-5xl leading-[0.95]">
                    {featuredEvent.date_label && (
                      <>
                        {featuredEvent.date_label}
                        <br />
                      </>
                    )}
                    <span className="text-[color:var(--nsa-orange)]">{featuredEvent.title}</span>
                  </h3>
                  {featuredEvent.excerpt && (
                    <p className="mt-4 text-white/80 max-w-xl leading-relaxed">
                      {featuredEvent.excerpt}
                    </p>
                  )}
                </div>
                <div className="flex-shrink-0 flex flex-col items-start md:items-end gap-3">
                  <span className="inline-flex items-center gap-3 bg-[color:var(--nsa-orange)] text-white px-6 py-4 label-eyebrow group-hover:bg-white group-hover:text-gray-900 transition-colors">
                    {featuredEvent.is_past ? "Guarda la gallery" : "Scopri di più"} <ArrowRight size={16} />
                  </span>
                </div>
              </div>
            </Link>

            <div className="mt-4 text-right">
              <Link
                to="/moto/eventi"
                className="inline-flex items-center gap-2 label-eyebrow text-xs text-gray-500 hover:text-[color:var(--nsa-orange)] transition-colors"
                data-testid="link-tutti-eventi"
              >
                Vedi tutti gli eventi <ArrowRight size={13} />
              </Link>
            </div>
          </motion.div>

          {/* Marquee strip delle foto evento (solo se la gallery ha foto) */}
          {Array.isArray(featuredEvent.gallery) && featuredEvent.gallery.length >= 6 && (
            <EventPhotoMarquee
              images={featuredEvent.gallery}
              to={`/moto/eventi/${featuredEvent.slug}#gallery`}
            />
          )}
        </Section>
      )}

      {/* Brand cards */}
      <Section>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <BrandCard to="/moto/ktm" logo="/logos/ktm.png" name="KTM" tagline="Ready to Race" bg="/showroom/ktm-showroom.jpg" tid="brand-ktm" />
          <BrandCard to="/moto/husqvarna" logo="/logos/husqvarna.png" name="Husqvarna Motorcycles" tagline="Pioneering since 1903" bg="/showroom/husqvarna-enduro.webp" tid="brand-husqvarna" />
          <BrandCard to="/moto/gasgas" logo="/logos/gasgas.png" name="GasGas Trial" tagline="Get on the Gas" bg="/showroom/gasgas-trial.webp" bgPosition="72% 40%" tid="brand-gasgas" />
          <RacingCard tid="brand-racing" />
        </div>
      </Section>

      {/* Test Ride — subito dopo le brand cards */}
      <Section tone="dark">
        <div className="grid md:grid-cols-5 gap-10 md:gap-16 items-start">
          <div className="md:col-span-2 text-white">
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">Test ride</span>
            <h2 className="font-display uppercase tracking-tight text-4xl md:text-5xl mt-3 leading-[0.95]">
              Prova la moto<br />dei tuoi sogni
            </h2>
            <p className="mt-6 text-white/80 leading-relaxed max-w-md">
              Test ride gratuito sui modelli disponibili in showroom.
              Prenota il tuo giro di prova in pochi click.
            </p>
          </div>
          <div className="md:col-span-3">
            <TestRideForm />
          </div>
        </div>
      </Section>

      {/* Showroom presentation */}
      <Section tone="gray">
        <div className="grid md:grid-cols-2 gap-10 md:gap-12 items-center">
          <div>
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">Showroom</span>
            <h2 className="font-display uppercase tracking-tight text-4xl md:text-5xl mt-3 leading-[0.95]">
              Uno showroom<br />che non dimenticherai
            </h2>
            <p className="mt-6 text-gray-700 leading-relaxed">
              La qualità indiscussa di <strong>KTM</strong> e <strong>Husqvarna Motorcycles</strong>, unita alla professionalità e all'esperienza di <strong>NSA MOTORS</strong>, ha permesso di realizzare uno spazio espositivo unico nella bergamasca.
            </p>
          </div>
          <div className="aspect-[4/3] bg-cover bg-center" style={{ backgroundImage: "url(/showroom/showroom-moto.webp), url(/placeholders/photo-slot.svg)" }} data-testid="photo-showroom" />
        </div>
      </Section>

      {/* Gamma moto */}
      <Section>
        <div className="grid md:grid-cols-2 gap-10 md:gap-12 items-center">
          <div className="aspect-[4/3] bg-cover bg-center md:order-2" style={{ backgroundImage: "url(/showroom/gamma-moto.webp), url(/placeholders/photo-slot.svg)" }} data-testid="photo-gamma" />
          <div>
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">La nostra gamma</span>
            <h2 className="font-display uppercase tracking-tight text-4xl md:text-5xl mt-3 leading-[0.95]">
              Nuovo, usato<br />&amp; multimarca
            </h2>
            <p className="mt-6 text-gray-700 leading-relaxed">
              Qui potrai trovare tutta la gamma di moto <strong>nuove ed usate KTM e Husqvarna Motorcycles</strong> e
              <strong> cinquantini Valenti Racing</strong>, oltre a diverse opportunità multimarca, una linea completa di
              <strong> E-Bicycles</strong> e tantissimi capi d'abbigliamento e accessori brandizzati.
            </p>
          </div>
        </div>
      </Section>

      {/* Officina moto */}
      <Section tone="dark">
        <div className="grid md:grid-cols-2 gap-10 md:gap-12 items-center">
          <div>
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">Officina</span>
            <h2 className="font-display uppercase text-4xl md:text-5xl mt-3 leading-[0.95]">
              Esperienza a 360°:<br />l'officina al lavoro
            </h2>
            <p className="mt-6 text-white/80 leading-relaxed max-w-xl">
              Manutenzioni, riparazioni meccaniche ed elettroniche, cambio gomme e revisioni.
              Autorizzati KTM e Husqvarna Motorcycles con <strong>ricambi ufficiali</strong>.
            </p>
            <Link to="/moto/officina" className="mt-8 bg-white text-gray-900 label-eyebrow px-6 py-4 inline-flex items-center gap-3 hover:bg-white/90">
              <Wrench size={16} /> SCOPRI L'OFFICINA <ArrowRight size={16} />
            </Link>
          </div>
          <div className="aspect-[4/3] bg-cover bg-center" style={{ backgroundImage: "url(/officina/img4.jpg)" }} data-testid="photo-officina" />
        </div>
      </Section>

      {/* Offerte online — Moto.it + Subito.it */}
      <Section>
        <div className="mb-10 max-w-3xl">
          <span className="label-eyebrow text-[color:var(--nsa-orange)]">Le offerte online</span>
          <h2 className="font-display uppercase tracking-tight text-4xl md:text-5xl mt-3 leading-[0.95]">
            Guarda il nostro catalogo<br />nuovo e usato
          </h2>
          <p className="mt-4 text-gray-700 leading-relaxed">
            Consulta le nostre offerte aggiornate ogni giorno sui portali di riferimento del settore.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:gap-6 max-w-4xl">
          <a
            href="https://dealer.moto.it/nsaservizi/Nuovo"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative aspect-[16/9] md:aspect-[21/9] overflow-hidden block bg-gray-900 text-white"
            data-testid="offerta-motoit"
          >
            {/* Photo slot (placeholder finché non arriva la foto reale) */}
            <div
              className="absolute inset-0 bg-cover bg-center scale-105 group-hover:scale-100 transition-transform duration-[900ms]"
              style={{ backgroundImage: "url(/showroom/offerta-motoit.webp), url(/placeholders/photo-slot.svg)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
            <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[color:var(--nsa-orange)] blur-3xl opacity-10 group-hover:opacity-25 transition-opacity duration-700" />
            <div className="relative h-full p-6 md:p-10 flex flex-col justify-between">
              <div className="flex items-start justify-between gap-4">
                <div className="bg-[#FFD400] px-3.5 py-2 md:px-4 md:py-2.5 shadow-lg transform group-hover:-translate-y-0.5 transition-transform">
                  <img src="/logos/moto-it.webp" alt="Moto.it" className="h-4 md:h-5 w-auto" />
                </div>
                <ExternalLink size={20} className="text-white/50 group-hover:text-[color:var(--nsa-orange)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition duration-300" />
              </div>
              <div>
                <h3 className="font-display uppercase text-2xl md:text-4xl leading-[0.95]">
                  Tutte le moto nuove<br />in tempo reale
                </h3>
                <p className="mt-3 text-white/75 text-sm md:text-base max-w-md">
                  Offerte, disponibilità e prezzi aggiornati sul portale di riferimento del settore.
                </p>
                <span className="mt-6 inline-flex items-center gap-2 label-eyebrow border border-white/30 px-4 py-2.5 group-hover:border-[color:var(--nsa-orange)] group-hover:text-[color:var(--nsa-orange)] transition-colors">
                  Sfoglia il dealer <ArrowRight size={14} />
                </span>
              </div>
            </div>
          </a>
        </div>
      </Section>

      {/* E-Bicycles */}
      <Section tone="gray">
        <div className="grid md:grid-cols-2 gap-10 md:gap-12 items-center">
          <div>
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">E-Bicycles</span>
            <h2 className="font-display uppercase tracking-tight text-4xl md:text-5xl mt-3 leading-[0.95]">
              Un salto nel futuro<br />della mobilità
            </h2>
            <p className="mt-6 text-gray-700 leading-relaxed">
              Sperimenta il perfetto equilibrio tra <strong>potenza, prestazioni e sostenibilità</strong>. Scopri un modo
              completamente nuovo di vivere l'emozione della guida, abbracciando un futuro ecologico e senza compromessi.
            </p>
          </div>
          <div className="aspect-[4/3] bg-cover bg-center" style={{ backgroundImage: "url(/showroom/ebicycles.jpg), url(/placeholders/photo-slot.svg)" }} data-testid="photo-ebikes" />
        </div>
      </Section>

      {/* Abbigliamento */}
      <Section>
        <div className="grid md:grid-cols-2 gap-10 md:gap-12 items-center">
          <div className="aspect-[4/3] bg-cover bg-center md:order-2" style={{ backgroundImage: "url(/showroom/apparel.webp), url(/placeholders/photo-slot.svg)" }} data-testid="photo-style" />
          <div>
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">Style</span>
            <h2 className="font-display uppercase tracking-tight text-4xl md:text-5xl mt-3 leading-[0.95]">
              Abbigliamento,<br />accessori & gadget
            </h2>
            <div className="mt-6 text-gray-700 leading-relaxed space-y-4">
              <p>
                All'interno del nostro showroom troverai un'ampissima gamma di abbigliamento, anche tecnico e specifico,
                originale <strong>KTM e Husqvarna Motorcycles</strong>, sia off-road che street ed e-bike.
              </p>
              <p>
                Ideale per i tuoi momenti di divertimento, per i tuoi viaggi in moto o da utilizzare nella vita di tutti i giorni,
                oltre ad accessori e gadget di altri famosi brand del settore come <strong>Motorex, Acerbis, Scott e Sidi</strong>.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA finale rimosso — sostituito dalla sezione Test Ride in alto */}
    </PageLayout>
  );
}

function BrandCard({ to, logo, name, tagline, bg, bgPosition = "center", tid }) {
  return (
    <Link to={to} className="group relative aspect-[4/5] overflow-hidden card-flat block" data-testid={tid}>
      <div
        className="absolute inset-0 bg-cover scale-110 group-hover:scale-100 transition-transform duration-[900ms]"
        style={{ backgroundImage: `url(${bg})`, backgroundPosition: bgPosition }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
      <div className="relative h-full flex flex-col justify-between p-6 text-white">
        <img src={logo} alt={name} className="h-12 md:h-14 w-auto object-contain object-left drop-shadow-lg" />
        <div>
          <span className="label-eyebrow text-white/85">{tagline}</span>
          <div className="font-display uppercase font-bold text-2xl md:text-3xl mt-1">{name}</div>
          <div className="mt-3 inline-flex items-center gap-2 label-eyebrow text-white/80 group-hover:text-white">
            Scopri <ArrowRight size={14} />
          </div>
        </div>
      </div>
    </Link>
  );
}

function RacingCard({ tid }) {
  return (
    <Link to="/moto/racing-team" className="group relative aspect-[4/5] overflow-hidden card-flat block bg-gray-900" data-testid={tid}>
      <div className="absolute inset-0 bg-cover bg-center opacity-70 scale-110 group-hover:scale-100 transition-transform duration-[900ms]" style={{ backgroundImage: "url(/racing/racing-hero.jpg)" }} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-black/30" />
      <div className="relative h-full flex flex-col justify-between p-6 text-white">
        <div className="w-9 h-9 md:w-10 md:h-10 bg-[color:var(--nsa-orange)] flex items-center justify-center shrink-0">
          <Trophy size={18} className="text-white" />
        </div>
        <div>
          <span className="label-eyebrow text-[color:var(--nsa-orange)]">Storia · Gallery · News</span>
          <div className="font-display uppercase font-bold text-2xl md:text-3xl mt-1">Racing Team</div>
          <div className="mt-3 inline-flex items-center gap-2 label-eyebrow text-white/80 group-hover:text-white">
            Segui il team <ArrowRight size={14} />
          </div>
        </div>
      </div>
    </Link>
  );
}


function EventPhotoMarquee({ images, to }) {
  // Take up to 24 photos for the strip, duplicate for seamless loop
  const picks = images.slice(0, 24);
  const strip = [...picks, ...picks];
  return (
    <Link
      to={to}
      className="group mt-8 block relative overflow-hidden"
      data-testid="event-photo-marquee"
      aria-label="Scorri le foto dell'evento"
    >
      <div className="nsa-marquee flex gap-3 group-hover:[animation-play-state:paused]">
        {strip.map((src, i) => (
          <div
            key={i}
            className="flex-shrink-0 w-48 md:w-64 aspect-[4/3] bg-gray-900 overflow-hidden"
          >
            <img
              src={mediaUrl(src)}
              alt={`Foto evento ${i + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        ))}
      </div>
      {/* fades laterali per effetto cinematografico */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-28 bg-gradient-to-r from-white to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-28 bg-gradient-to-l from-white to-transparent" />
    </Link>
  );
}
